import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {DeploymentWorkflowService} from "./deployment-workflow.service";
import {SelectableAsset, selectableAssetModelListFactory} from "../../types/selectable-asset";
import {WorkflowStep} from "../workflow-step";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {RejectDeploymentDialogComponent} from "./reject-deployment-dialog.component";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {DeploymentService} from "../../deployment/deployment.service";
import {DeploymentGqlModel} from "../../deployment/deployment.gql-model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

function tokenizeString(str, tokenizers = "\\s") {
  if (!str) return [];
  const regex = new RegExp(`(?:[^${tokenizers}"]+|"[^"]*")+`, "g");
  return str.match(regex);
}

function stripQuotes(str) {
  if (!str || !str.trim()) return "";
  const result = str.match(/^"(.*)"$/);
  if (result && result.length > 1) return result[1];
  return str;
}

function cleanParam(param = "") {
  const clean = ((typeof param) !== "string") ? `${param}` : param;
  return clean.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
}

@Component({
  templateUrl: "./send-assets-to-sap.template.pug",
  providers: [DeploymentWorkflowService],
})
@exposeComponentInDebugMode()
export class SendAssetsToSAPComponent extends WorkflowStep {
  disableStep: boolean = false;
  error: Error;
  disableAll: boolean = false;
  deploymentId: string = "";
  assets: SelectableAsset[] = [];
  filterAssets: SelectableAsset[] = [];
  displayAssets: SelectableAsset[] = [];
  stepIsComplete: boolean = true;
  deployment: DeploymentGqlModel = new DeploymentGqlModel({});

  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  searchText: string = "";

  ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.error = undefined;
    this.deploymentId = workflowTargetInfo.targetId;
    this.loadData();
  }

  private loadList(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.filterAssets = this.assets.filter((asset) => {
      if (this.searchText) {
        const values = [
          asset.id,
          asset.serialNumber,
          asset.assetTag,
          asset.building,
          asset.floor,
          asset.floorMapId,
          asset.productNumber,
        ];

        const searchFailures = tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => stripQuotes(v)) // strip quotes on ends if present
          .map(v => cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }
      return true;
    });
    this.displayAssets = this.filterAssets.slice(start, end);
    this.recalculateMaxPages();
  }

  @handleErrors()
  async updateCompleteStatus(): Promise<void> {
    if (this.assets.every(it => it.sentToSAP)) {
      const status = this.workflowService.getCurrentStepStatus();
      if (status !== WorkflowStepStatus.Completed && status !== WorkflowStepStatus.Blocked) {
        await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
      }
      this.stepIsComplete = true;
    } else {
      const status = this.workflowService.getCurrentStepStatus();
      if (status === WorkflowStepStatus.Completed) {
        await this.workflowService.setStepStatus(WorkflowStepStatus.InProgress);
      }

      this.stepIsComplete = false;
    }
  }

  private async loadData(): Promise<void> {
    if (!this.workflowTargetInfo.targetId) return;
    this.deploymentId = this.workflowTargetInfo.targetId;

    this.loading = true;
    this.assets = [];
    this.loadList();
    const {rows} = await this.deploymentWorkflowService.getAssetForSapUploadList(this.workflowTargetInfo.targetId)
      .toPromise();

    this.assets = selectableAssetModelListFactory(rows, {});
    this.recalculateMaxPages();

    await this.updateCompleteStatus();
    this.deployment = await this.deploymentService.getDeploymentById(this.deploymentId);
    this.loadList();
    this.loading = false;
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.filterAssets.length / (+this.pageSize));
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        break;
    }
    this.loadList();
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public deploymentWorkflowService: DeploymentWorkflowService,
    private dialogService: DialogService,
    private deploymentService: DeploymentService,
  ) {
    super();
  }

  @handleErrors()
  async deselectAll(): Promise<void> {
    await this.deploymentWorkflowService.updateSentToSAP(this.deploymentId, false).toPromise();
    this.assets.forEach((it) => {
      if (it.installed) it.sentToSAP = false;
    });
    await this.updateCompleteStatus();
  }

  @handleErrors()
  async selectAll(): Promise<void> {
    await this.deploymentWorkflowService.updateSentToSAP(this.deploymentId, true).toPromise();
    this.assets.forEach((it) => {
      if (it.installed) it.sentToSAP = true;
    });
    await this.updateCompleteStatus();
  }

  @handleErrors()
  async updateSelection(asset): Promise<void> {
    if (asset.installed) {
      await this.deploymentWorkflowService.updateSentToSAP(this.deploymentId, asset.sentToSAP, asset.id).toPromise();
    } else {
      asset.sentToSAP = false;
    }
    await this.updateCompleteStatus();
  }

  @handleErrors()
  public rejectDeployment(): void {
    RejectDeploymentDialogComponent.showRejectDeploymentDialogModal(
      this.dialogService,
      {
        deploymentId: this.deploymentId,
        workflowId: this.workflowService.workflowConfig.id,
      },
    )
      .subscribe((result: any) => {
        if (result.success) {
          // navigate to the Deployments screen for this project (The CA is done with this deployment)
          this.router.navigate(["/deployments"], {queryParams: {projectId: this.workflowTargetInfo.projectId}});
        }
      });
  }
}
